// Angel One WebSocket Manager
// Uses V1 protocol (browser-compatible — no custom headers needed)
// Falls back gracefully if WebSocket is unavailable

const WS_V1_URL = 'wss://wsfeeds.angelbroking.com/NestHtml5Mobile/socket/stream';
const WS_V2_URL = 'wss://smartapisocket.angelone.in/smart-stream';

class AngelOneWebSocket {
  constructor() {
    this.ws = null;
    this.feedToken = null;
    this.clientcode = null;
    this.subscriptions = new Map();
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnect = 3;
    this.heartbeatInterval = null;
    this.isConnected = false;
  }

  connect(feedToken, clientcode) {
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }

    this.feedToken = feedToken;
    this.clientcode = clientcode;
    this.reconnectAttempts = 0;

    this._tryConnect();
  }

  _tryConnect() {
    try {
      // Try V2 endpoint first, fall back to V1
      const url = this.reconnectAttempts > 0 ? WS_V1_URL : WS_V2_URL;
      this.ws = new WebSocket(url);
      this.ws.binaryType = 'arraybuffer';

      this.ws.onopen = () => {
        this.isConnected = true;
        this.reconnectAttempts = 0;

        // Send V1-style auth message (works from browser)
        const authMsg = JSON.stringify({
          task: 'cn',
          channel: '',
          token: this.feedToken,
          user: this.clientcode,
          acctid: this.clientcode,
        });
        this.ws.send(authMsg);

        this._startHeartbeat();
        this._resubscribe();
        this._emit('connected');
      };

      this.ws.onmessage = (event) => {
        if (typeof event.data === 'string') {
          this._handleTextMessage(event.data);
        } else if (event.data instanceof ArrayBuffer) {
          this._handleBinaryMessage(event.data);
        }
      };

      this.ws.onerror = (error) => {
        console.warn('WebSocket error (non-critical):', error);
      };

      this.ws.onclose = (event) => {
        this.isConnected = false;
        this._stopHeartbeat();
        this._emit('disconnected');
        this._attemptReconnect();
      };
    } catch (error) {
      console.warn('WebSocket connection failed (non-critical):', error);
      this._emit('disconnected');
    }
  }

  disconnect() {
    this.reconnectAttempts = this.maxReconnect;
    this._stopHeartbeat();
    if (this.ws) {
      try { this.ws.close(); } catch (e) { /* ok */ }
      this.ws = null;
    }
    this.isConnected = false;
    this.subscriptions.clear();
    this._emit('disconnected');
  }

  subscribe(tokens, mode = 1, callback) {
    if (!tokens || tokens.length === 0) return;

    tokens.forEach(t => {
      this.subscriptions.set(`${t.exchange}_${t.token}`, { ...t, mode, callback });
    });

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this._sendSubscription(tokens, mode);
    }
  }

  unsubscribe(tokens) {
    if (!tokens || tokens.length === 0) return;

    tokens.forEach(t => {
      this.subscriptions.delete(`${t.exchange}_${t.token}`);
    });

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this._sendUnsubscription(tokens);
    }
  }

  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);
    return () => this.listeners.get(event)?.delete(callback);
  }

  _emit(event, data) {
    this.listeners.get(event)?.forEach(cb => {
      try { cb(data); } catch (e) { console.warn('WebSocket listener error:', e); }
    });
  }

  _sendSubscription(tokens, mode) {
    try {
      // V2 format
      const exchangeTokens = {};
      tokens.forEach(t => {
        const exNum = this._exchangeToNum(t.exchange);
        if (!exchangeTokens[exNum]) exchangeTokens[exNum] = [];
        exchangeTokens[exNum].push(String(t.token));
      });

      const tokenList = Object.entries(exchangeTokens).map(([exchangeType, tkns]) => ({
        exchangeType: parseInt(exchangeType),
        tokens: tkns,
      }));

      const msg = JSON.stringify({
        action: 1,
        params: { mode, tokenList },
      });
      this.ws.send(msg);
    } catch (e) {
      // Try V1 format as fallback
      try {
        const channel = tokens.map(t => {
          const seg = this._exchangeToSegment(t.exchange);
          return `${seg}|${t.token}`;
        }).join('&');

        const msg = JSON.stringify({
          task: 'mw',
          channel,
          token: this.feedToken,
          user: this.clientcode,
          acctid: this.clientcode,
        });
        this.ws.send(msg);
      } catch (e2) {
        console.warn('Subscription failed:', e2);
      }
    }
  }

  _sendUnsubscription(tokens) {
    try {
      const exchangeTokens = {};
      tokens.forEach(t => {
        const exNum = this._exchangeToNum(t.exchange);
        if (!exchangeTokens[exNum]) exchangeTokens[exNum] = [];
        exchangeTokens[exNum].push(String(t.token));
      });

      const tokenList = Object.entries(exchangeTokens).map(([exchangeType, tkns]) => ({
        exchangeType: parseInt(exchangeType),
        tokens: tkns,
      }));

      this.ws.send(JSON.stringify({
        action: 0,
        params: { mode: 1, tokenList },
      }));
    } catch (e) {
      // Silent
    }
  }

  _resubscribe() {
    const grouped = {};
    this.subscriptions.forEach(sub => {
      const key = sub.mode || 1;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push({ exchange: sub.exchange, token: sub.token });
    });

    Object.entries(grouped).forEach(([mode, tokens]) => {
      this._sendSubscription(tokens, parseInt(mode));
    });
  }

  _handleTextMessage(data) {
    try {
      const msg = JSON.parse(data);
      if (msg.type === 'order_update') {
        this._emit('orderUpdate', msg);
      }
    } catch (e) {
      // Heartbeat ack or other text — ignore
    }
  }

  _handleBinaryMessage(buffer) {
    try {
      if (buffer.byteLength < 47) return; // Too small for even LTP data

      const view = new DataView(buffer);
      const mode = view.getInt8(0);
      const exchangeType = view.getInt8(1);
      const token = this._readToken(view, 2);
      const ltp = view.getInt32(43, true) / 100;

      const tick = {
        token,
        exchange: this._numToExchange(exchangeType),
        ltp,
        mode,
      };

      // Quote mode — add OHLC + volume
      if (buffer.byteLength >= 123 && mode >= 2) {
        tick.lastQty = view.getInt32(51, true);
        tick.avgPrice = view.getInt32(59, true) / 100;
        tick.volume = view.getInt32(67, true);
        tick.totalBuyQty = view.getFloat64(75, true);
        tick.totalSellQty = view.getFloat64(83, true);
        tick.open = view.getInt32(91, true) / 100;
        tick.high = view.getInt32(99, true) / 100;
        tick.low = view.getInt32(107, true) / 100;
        tick.close = view.getInt32(115, true) / 100;
      }

      this._emit('tick', tick);
    } catch (e) {
      // Ignore malformed binary data
    }
  }

  _readToken(view, offset) {
    const bytes = [];
    for (let i = 0; i < 25; i++) {
      const b = view.getUint8(offset + i);
      if (b === 0) break;
      bytes.push(b);
    }
    return String.fromCharCode(...bytes).trim();
  }

  _exchangeToNum(exchange) {
    const map = { NSE: 1, NFO: 2, BSE: 3, BFO: 4, MCX: 5, CDS: 13 };
    return map[exchange] || 1;
  }

  _numToExchange(num) {
    const map = { 1: 'NSE', 2: 'NFO', 3: 'BSE', 4: 'BFO', 5: 'MCX', 13: 'CDS' };
    return map[num] || 'NSE';
  }

  _exchangeToSegment(exchange) {
    const map = { NSE: 'nse_cm', NFO: 'nse_fo', BSE: 'bse_cm', BFO: 'bse_fo', MCX: 'mcx_fo', CDS: 'cds_fo' };
    return map[exchange] || 'nse_cm';
  }

  _startHeartbeat() {
    this._stopHeartbeat();
    this.heartbeatInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        try { this.ws.send('ping'); } catch (e) { /* ok */ }
      }
    }, 10000); // 10 seconds per official docs
  }

  _stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  _attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnect) return;
    if (!this.feedToken || !this.clientcode) return;

    this.reconnectAttempts++;
    const delay = Math.min(2000 * Math.pow(2, this.reconnectAttempts), 30000);
    setTimeout(() => this._tryConnect(), delay);
  }
}

// Singleton
const wsManager = new AngelOneWebSocket();
export default wsManager;
